import { useEffect, useState, useCallback } from 'react';
import {
  Settings,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Plus,
  Trash2,
  Camera,
  Activity,
  MessageSquare,
} from 'lucide-react';
import { siteProxy } from '../../services/siteProxy';

// ─── Types ───────────────────────────────────────────────────────────

interface PhotoArticle {
  id: number;
  offer_id: string;
  product_name: string;
  photos_count: number;
}

interface ChatTemplate {
  id: number;
  title: string;
  template_text: string;
  sort_order: number;
  is_default: number;
}

interface OzonStats {
  total: number;
  awaiting_packaging: number;
  awaiting_deliver: number;
  photo_red: number;
  photo_yellow: number;
  photo_green: number;
}

// ─── Collapsible Section ─────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-slate-800/40 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <Icon className="w-4.5 h-4.5 text-slate-400" />
        <span className="text-base font-medium text-slate-200 flex-1">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ─── OzonLabPage ─────────────────────────────────────────────────────

export function OzonLabPage() {
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Ozon Lab</h1>
        <p className="text-sm text-slate-500 mt-1">Управление FBS заказами Ozon</p>
      </div>

      <Section title="Статистика" icon={Activity} defaultOpen>
        <StatsPanel />
      </Section>

      <Section title="Основные настройки" icon={Settings}>
        <GeneralSettingsPanel />
      </Section>

      <Section title="Фото-артикулы" icon={Camera} defaultOpen>
        <PhotoArticlesPanel />
      </Section>

      <Section title="Быстрые ответы (чат)" icon={MessageSquare}>
        <QuickRepliesPanel />
      </Section>
    </div>
  );
}

// ─── Stats Panel ─────────────────────────────────────────────────────

function StatsPanel() {
  const [stats, setStats] = useState<OzonStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ozon', '/api/orders/stats')) as {
        success: boolean;
        data: OzonStats;
      };
      setStats(data.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-slate-500">Загрузка…</p>;
  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!stats) return null;

  const items = [
    { label: 'Всего заказов', value: stats.total, color: 'text-slate-200' },
    { label: 'Ожидают сборки', value: stats.awaiting_packaging, color: 'text-yellow-400' },
    { label: 'Ожидают отгрузки', value: stats.awaiting_deliver, color: 'text-blue-400' },
    { label: 'Фото 🔴', value: stats.photo_red, color: 'text-red-400' },
    { label: 'Фото 🟡', value: stats.photo_yellow, color: 'text-yellow-400' },
    { label: 'Фото 🟢', value: stats.photo_green, color: 'text-emerald-400' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {items.map((it) => (
        <div key={it.label} className="bg-slate-900/50 rounded-lg p-3 border border-white/[0.04]">
          <p className="text-xs text-slate-500 mb-1">{it.label}</p>
          <p className={`text-xl font-bold ${it.color}`}>{it.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── General Settings Panel ──────────────────────────────────────────

function GeneralSettingsPanel() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ozon', '/api/settings')) as {
        success: boolean;
        data: { key: string; value: string }[];
      };
      const map: Record<string, string> = {};
      data.data?.forEach((s) => { map[s.key] = s.value; });
      setSettings(map);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-slate-500">Загрузка…</p>;
  if (error) return <p className="text-sm text-red-400">{error}</p>;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-slate-400 mb-1">Ozon Client ID</label>
        <input
          className="w-full bg-slate-900/50 border border-white/[0.06] text-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none"
          placeholder="Задаётся через .env на сервере Ozon Lab"
          disabled
        />
        <p className="text-xs text-slate-600 mt-1">Управляется через переменные окружения (OZON_CLIENT_ID)</p>
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Ozon API Key</label>
        <input
          className="w-full bg-slate-900/50 border border-white/[0.06] text-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none"
          placeholder="***"
          disabled
        />
        <p className="text-xs text-slate-600 mt-1">Управляется через переменные окружения (OZON_API_KEY)</p>
      </div>
      {Object.keys(settings).length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <p className="text-xs text-slate-500 font-medium uppercase mb-2">Текущие настройки в БД</p>
          {Object.entries(settings).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-slate-400 font-mono">{k}</span>
              <span className="text-slate-300">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Photo Articles Panel ────────────────────────────────────────────

function PhotoArticlesPanel() {
  const [articles, setArticles] = useState<PhotoArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newOfferId, setNewOfferId] = useState('');
  const [newProductName, setNewProductName] = useState('');
  const [newPhotosCount, setNewPhotosCount] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ozon', '/api/settings/photo-articles')) as {
        success: boolean;
        data: PhotoArticle[];
      };
      setArticles(data.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newOfferId.trim()) return;
    setSaving(true);
    try {
      await siteProxy('ozon', '/api/settings/photo-articles', {
        method: 'POST',
        body: { offer_id: newOfferId.trim(), product_name: newProductName.trim(), photos_count: newPhotosCount },
      });
      setNewOfferId('');
      setNewProductName('');
      setNewPhotosCount(1);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await siteProxy('ozon', `/api/settings/photo-articles/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Загрузка…</p>;
  if (error) return (
    <div>
      <p className="text-sm text-red-400 mb-2">{error}</p>
      <button onClick={load} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Повторить
      </button>
    </div>
  );

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Заказы с этими артикулами автоматически помечаются как требующие фото
      </p>

      {/* Add form */}
      <div className="flex gap-2 mb-4">
        <input
          value={newOfferId}
          onChange={(e) => setNewOfferId(e.target.value)}
          className="flex-1 bg-slate-900/50 border border-white/[0.06] text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/30"
          placeholder="Offer ID (артикул)"
        />
        <input
          value={newProductName}
          onChange={(e) => setNewProductName(e.target.value)}
          className="flex-1 bg-slate-900/50 border border-white/[0.06] text-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
          placeholder="Название (опционально)"
        />
        <input
          type="number"
          min={1}
          max={6}
          value={newPhotosCount}
          onChange={(e) => setNewPhotosCount(parseInt(e.target.value) || 1)}
          className="w-16 bg-slate-900/50 border border-white/[0.06] text-slate-200 rounded-lg px-2 py-2 text-sm outline-none text-center"
        />
        <button
          onClick={handleAdd}
          disabled={!newOfferId.trim() || saving}
          className="px-3 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      {articles.length === 0 ? (
        <p className="text-sm text-slate-600 py-3 text-center">Нет артикулов</p>
      ) : (
        <div className="space-y-1.5">
          {articles.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-slate-900/30 rounded-lg px-4 py-2.5 border border-white/[0.04]">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-slate-200">{a.offer_id}</span>
                {a.product_name && <span className="text-sm text-slate-500">{a.product_name}</span>}
              </div>
              <button
                onClick={() => handleDelete(a.id)}
                className="text-slate-600 hover:text-red-400 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Quick Replies Panel ─────────────────────────────────────────────

function QuickRepliesPanel() {
  const [templates, setTemplates] = useState<ChatTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ozon', '/api/chat/templates/all')) as {
        success: boolean;
        data: ChatTemplate[];
      };
      setTemplates(data.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newTitle.trim() || !newText.trim()) return;
    setSaving(true);
    try {
      await siteProxy('ozon', '/api/chat/templates', {
        method: 'POST',
        body: { title: newTitle.trim(), text: newText.trim(), sortOrder: templates.length + 1 },
      });
      setNewTitle('');
      setNewText('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await siteProxy('ozon', `/api/chat/templates/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Загрузка…</p>;
  if (error) return (
    <div>
      <p className="text-sm text-red-400 mb-2">{error}</p>
      <button onClick={load} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Повторить
      </button>
    </div>
  );

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Шаблоны быстрых ответов, доступные в чат-панели при работе с фото
      </p>

      {/* Add form */}
      <div className="flex gap-2 mb-4">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          className="w-40 bg-slate-900/50 border border-white/[0.06] text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/30"
          placeholder="Название"
        />
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          className="flex-1 bg-slate-900/50 border border-white/[0.06] text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/30"
          placeholder="Текст шаблона"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button
          onClick={handleAdd}
          disabled={!newTitle.trim() || !newText.trim() || saving}
          className="px-3 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      {templates.length === 0 ? (
        <p className="text-sm text-slate-600 py-3 text-center">Нет шаблонов</p>
      ) : (
        <div className="space-y-1.5">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between bg-slate-900/30 rounded-lg px-4 py-2.5 border border-white/[0.04]">
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-slate-300">{t.title}</span>
                <p className="text-xs text-slate-500 truncate mt-0.5">{t.template_text}</p>
              </div>
              <div className="flex items-center gap-2 ml-3 shrink-0">
                <button
                  onClick={() => handleDelete(t.id)}
                  className="text-slate-600 hover:text-red-400 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
